/**
 * Data layer for the mobile player.
 *
 * Source priority:
 *  1. The editor's SERVER LIBRARY (/api/flow/charts) when a backend is reachable
 *     — so charts you save in the editor show up in the phone game.
 *  2. The STATIC bundle (public/beatmaps/*.flow + manifest.json) as a fallback,
 *     so the player still works with no backend (e.g. a static host).
 *
 * Each .flow is a self-contained zip (chart + audio); audio is extracted from
 * the bundle, not fetched separately.
 */
import { unpackBundle } from '../editors/flow/flowBundle';
import { getApiBase } from '../api/apiBase';

const BEATMAPS = '/beatmaps';

export interface PlayerSongEntry {
  file: string;        // server chart name, or "BLACKPINK.flow" for static
  name: string;
  author: string;
  source: 'server' | 'static';
}

export interface PlayerDifficulty {
  name: 'Easy' | 'Medium' | 'Hard' | 'Expert';
  difficulty: number;
  noteCount: number;
}

export interface PlayerSong {
  name: string;
  author: string;
  bpm: number;
  startOffset: number;
  audioDuration: number;
  difficulties: PlayerDifficulty[];
  raw: any;
  /** Object URL for the audio extracted from the .flow bundle (or null). */
  audioUrl: string | null;
}

function prettify(name: string): string {
  return name.replace(/\.flow$/i, '').replace(/[_]+/g, ' ').trim();
}

/** Try the editor's server library. Returns null if no reachable backend. */
async function listServerSongs(): Promise<PlayerSongEntry[] | null> {
  try {
    const res = await fetch(`${getApiBase()}/api/flow/charts`, { cache: 'no-cache' });
    if (!res.ok) return null;
    if (!(res.headers.get('content-type') || '').includes('json')) return null; // static host → index.html
    const json = await res.json();
    const charts = Array.isArray(json.charts) ? json.charts : [];
    if (charts.length === 0) return null;   // empty library → fall back to bundled demo songs
    return charts.map((c: any) => ({ file: c.name, name: prettify(c.name), author: '', source: 'server' as const }));
  } catch {
    return null;
  }
}

/** List songs — server library first, static bundle as fallback. */
export async function listSongs(): Promise<PlayerSongEntry[]> {
  const server = await listServerSongs();
  if (server && server.length) return server;

  const res = await fetch(`${BEATMAPS}/manifest.json`, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Could not load the song list (HTTP ${res.status}).`);
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('json')) throw new Error('Song list not found (no bundled beatmaps).');
  const json = await res.json();
  return (Array.isArray(json.songs) ? json.songs : []).map((s: any) => ({ ...s, source: 'static' as const }));
}

async function fetchBundle(entry: PlayerSongEntry): Promise<ArrayBuffer> {
  const url = entry.source === 'server'
    ? `${getApiBase()}/api/flow/charts/${encodeURIComponent(entry.file)}`
    : `${BEATMAPS}/${encodeURIComponent(entry.file)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not load song (HTTP ${res.status}).`);
  return res.arrayBuffer();
}

/** Fetch + unpack a .flow: chart + difficulties + audio object URL. */
export async function loadSong(entry: PlayerSongEntry): Promise<PlayerSong> {
  const buf = await fetchBundle(entry);
  const { chartJson, audio } = await unpackBundle(buf);
  const chart = JSON.parse(chartJson);

  // Keep ALL difficulties (even empty ones) so the menu shows the full set;
  // empty ones are shown disabled in the UI.
  const diffs: PlayerDifficulty[] = (chart.difficulties || [])
    .map((d: any) => ({
      name: d.name,
      difficulty: d.difficulty ?? 0,
      noteCount: (d.taps?.length || 0) + (d.rails?.length || 0) + (d.flicks?.length || 0),
    }))
    .sort((a: PlayerDifficulty, b: PlayerDifficulty) => a.difficulty - b.difficulty);

  let audioUrl: string | null = null;
  if (audio?.bytes) {
    audioUrl = URL.createObjectURL(new Blob([audio.bytes], { type: audio.mime || 'audio/mpeg' }));
  }

  return {
    name: chart.name || prettify(entry.file),
    author: chart.author || '',
    bpm: chart.bpm || 120,
    startOffset: chart.startOffset || 0,
    audioDuration: chart.audioDuration || 0,
    difficulties: diffs,
    raw: chart,
    audioUrl,
  };
}
